/**
 * Utilitários de Otimização para Dashboard
 * 
 * Este arquivo contém classes e hooks para otimizar o desempenho
 * do dashboard, incluindo cache inteligente e processamento em lotes.
 */

export interface CachedResult {
  data: any;
  timestamp: number;
}

export interface LeadsByPeriodResult {
  totalLeads: number;
  periodLabel: string;
  filteredLeads: any[];
}

export interface ConversionByPeriodResult {
  conversionRate: number;
  convertedLeads: number;
  totalLeads: number;
  periodLabel: string;
}

export class DashboardOptimizer {
  private static instance: DashboardOptimizer;
  private cache: Map<string, CachedResult> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

  public static getInstance(): DashboardOptimizer {
    if (!DashboardOptimizer.instance) {
      DashboardOptimizer.instance = new DashboardOptimizer();
    }
    return DashboardOptimizer.instance;
  }

  /**
   * Processamento em lotes para grandes volumes de dados
   */
  public processBatch<T>(items: T[], batchSize: number, processor: (batch: T[]) => T[]): T[] {
    const results: T[] = [];
    
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const processedBatch = processor(batch);
      results.push(...processedBatch);
    }
    
    return results;
  }

  /**
   * Cache inteligente com TTL
   */
  public getCached<T>(key: string, fetcher: () => T): T {
    const cached = this.cache.get(key);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      return cached.data;
    }
    
    const data = fetcher();
    this.cache.set(key, { data, timestamp: now });
    return data;
  }

  /**
   * Filtro otimizado por período
   */
  public filterLeadsByPeriod(leads: any[], period: string, customStart?: string, customEnd?: string): any[] {
    const cacheKey = `leads_${period}_${customStart || ''}_${customEnd || ''}`;
    
    return this.getCached(cacheKey, () => {
      const BATCH_SIZE = 1000;
      
      if (leads.length <= BATCH_SIZE) {
        return this.filterBatch(leads, period, customStart, customEnd);
      }
      
      // Processar em lotes para grandes volumes
      const results: any[] = [];
      for (let i = 0; i < leads.length; i += BATCH_SIZE) {
        const batch = leads.slice(i, i + BATCH_SIZE);
        results.push(...this.filterBatch(batch, period, customStart, customEnd));
      }
      
      return results;
    });
  }

  /**
   * Cálculo otimizado de taxa de conversão
   */
  public calculateConversionRate(leads: any[], period: string, customStart?: string, customEnd?: string): ConversionByPeriodResult {
    const cacheKey = `conversion_${period}_${customStart || ''}_${customEnd || ''}`;
    
    return this.getCached(cacheKey, () => {
      const filteredLeads = this.filterLeadsByPeriod(leads, period, customStart, customEnd);
      
      // Contar conversões de forma otimizada
      const conversions = filteredLeads.filter((lead: any) => {
        return lead.crm_stages && lead.crm_stages.stage_order === 4;
      }).length;
      
      const rate = filteredLeads.length > 0 ? (conversions / filteredLeads.length) * 100 : 0;
      
      return {
        conversionRate: Math.round(rate * 100) / 100,
        convertedLeads: conversions,
        totalLeads: filteredLeads.length,
        periodLabel: this.getPeriodLabel(period, customStart, customEnd)
      };
    });
  }

  /**
   * Filtro de lote otimizado
   */
  private filterBatch(leads: any[], period: string, customStart?: string, customEnd?: string): any[] {
    const now = new Date();
    let startDate: Date;
    let endDate: Date;

    if (customStart && customEnd) {
      startDate = new Date(customStart);
      endDate = new Date(customEnd);
    } else {
      switch (period) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
          break;
        case 'yesterday':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'this_week':
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - now.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          startDate = startOfWeek;
          endDate = new Date(startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000);
          break;
        case 'last_week':
          const lastWeekStart = new Date(now);
          lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
          lastWeekStart.setHours(0, 0, 0, 0);
          startDate = lastWeekStart;
          endDate = new Date(lastWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
          break;
        case 'this_month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          break;
        case 'last_month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'this_year':
          startDate = new Date(now.getFullYear(), 0, 1);
          endDate = new Date(now.getFullYear() + 1, 0, 1);
          break;
        case 'last_year':
          startDate = new Date(now.getFullYear() - 1, 0, 1);
          endDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = new Date(0);
          endDate = new Date();
      }
    }

    return leads.filter((lead: any) => {
      const leadDate = new Date(lead.created_at || lead.updated_at);
      return leadDate >= startDate && leadDate < endDate;
    });
  }

  /**
   * Gerar label do período
   */
  private getPeriodLabel(period: string, customStart?: string, customEnd?: string): string {
    if (customStart && customEnd) {
      const start = new Date(customStart);
      const end = new Date(customEnd);
      return `${start.toLocaleDateString('pt-BR')} - ${end.toLocaleDateString('pt-BR')}`;
    }

    const labels: { [key: string]: string } = {
      today: 'Hoje',
      yesterday: 'Ontem',
      this_week: 'Esta Semana',
      last_week: 'Semana Passada',
      this_month: 'Este Mês',
      last_month: 'Mês Passado',
      this_year: 'Este Ano',
      last_year: 'Ano Passado'
    };

    return labels[period] || period;
  }

  /**
   * Limpar cache expirado
   */
  public cleanupCache(): void {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    for (const [key, value] of entries) {
      if ((now - value.timestamp) > this.CACHE_DURATION) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Limpar todo o cache
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Obter estatísticas do cache
   */
  public getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

/**
 * Hook personalizado para otimização do dashboard
 */
export const useDashboardOptimization = () => {
  const optimizer = DashboardOptimizer.getInstance();
  
  return {
    filterLeadsByPeriod: optimizer.filterLeadsByPeriod.bind(optimizer),
    calculateConversionRate: optimizer.calculateConversionRate.bind(optimizer),
    cleanupCache: optimizer.cleanupCache.bind(optimizer),
    clearCache: optimizer.clearCache.bind(optimizer),
    getCacheStats: optimizer.getCacheStats.bind(optimizer)
  };
};

/**
 * Utilitário para debounce de funções
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Utilitário para throttle de funções
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};
